import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SparklesIcon, LoaderIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, TextAreaField, DisplayField } from '../components/FormComponents';
import { mockProducts, masterDataSets } from '../data/mockData';
export function ProductForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const mode = id ? 'edit' : 'create';
  const initialData = mode === 'edit' && id ? mockProducts.find(p => p.id === id) : null;
  const [formData, setFormData] = useState<any>(initialData || {
    productCode: 'PROD-AUTO',
    productName: '',
    productType: '',
    category: '',
    subCategory: '',
    unitOfMeasure: '',
    defaultRate: '',
    warranty: '',
    status: 'Active',
    notes: ''
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleAiFill = async () => {
    if (!formData.productName.trim()) {
      setAiError('Please enter a product name first.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: `You are a procurement product data assistant. Given a product name, suggest appropriate values for a product catalog entry. Respond ONLY with a valid JSON object (no markdown, no code fences) with these exact keys:
- "productType": one of "Raw Material", "Finished Goods", "Consumable", "Service"
- "category": one of "Stationary", "Food", "Electronics", "Others"
- "unitOfMeasure": one of "pcs", "box", "kg", "lot"
- "defaultRate": a reasonable numeric price estimate (number only)
- "warranty": a warranty period string like "1 Year", "6 Months", "No Warranty"
- "notes": a brief 1-2 sentence product description`
          }, {
            role: 'user',
            content: `Product name: "${formData.productName}"`
          }],
          temperature: 0.7,
          max_tokens: 300
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get AI suggestions. Please try again.');
      }
      const data = await response.json();
      const content = data.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('No suggestions received from AI.');
      }
      const suggestions = JSON.parse(content);
      setFormData((prev: any) => ({
        ...prev,
        productType: suggestions.productType || prev.productType,
        category: suggestions.category || prev.category,
        unitOfMeasure: suggestions.unitOfMeasure || prev.unitOfMeasure,
        defaultRate: suggestions.defaultRate?.toString() || prev.defaultRate,
        warranty: suggestions.warranty || prev.warranty,
        notes: suggestions.notes || prev.notes
      }));
    } catch (err: any) {
      setAiError(err.message || 'Something went wrong with AI suggestions.');
    } finally {
      setAiLoading(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Product ${mode === 'create' ? 'created' : 'updated'} successfully!`);
    navigate('/products');
  };
  return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-1430">
      <div className="flex items-center justify-between" data-id="element-1431">
        <div data-id="element-1432">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1433">
            {mode === 'create' ? 'Create Product' : 'Edit Product'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1434">
            Configure product details and specifications.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1435">
        <FormSection title="Basic Information" data-id="element-1436">
          {mode === 'edit' ? <DisplayField label="Product Code" value={formData.productCode} data-id="element-1437" /> : <TextField label="Product Code" name="productCode" value={formData.productCode} disabled required data-id="element-1438" />}
          <div data-id="element-1439">
            <TextField label="Product Name" name="productName" value={formData.productName} onChange={handleChange} required data-id="element-1440" />
            <div className="mt-2 flex items-center gap-3" data-id="element-1441">
              <button type="button" onClick={handleAiFill} disabled={aiLoading || !formData.productName.trim()} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-1442">
                {aiLoading ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1443" /> : <SparklesIcon className="w-4 h-4" data-id="element-1444" />}
                {aiLoading ? 'Suggesting...' : 'Auto-fill with AI'}
              </button>
              {aiError && <span className="text-sm text-red-600" data-id="element-1445">{aiError}</span>}
            </div>
          </div>

          <SelectField label="Product Category" name="productType" value={formData.productType} onChange={handleChange} required options={[{
          value: 'Raw Material',
          label: 'Raw Material'
        }, {
          value: 'Finished Goods',
          label: 'Finished Goods'
        }, {
          value: 'Consumable',
          label: 'Consumable'
        }, {
          value: 'Service',
          label: 'Service'
        }]} data-id="element-1446" />
          <SelectField label="Product Sub Category" name="category" value={formData.category} onChange={handleChange} required options={[{
          value: 'Stationary',
          label: 'Stationary'
        }, {
          value: 'Food',
          label: 'Food'
        }, {
          value: 'Electronics',
          label: 'Electronics'
        }, {
          value: 'Others',
          label: 'Others'
        }]} data-id="element-1447" />
          <SelectField label="Unit of Measure" name="unitOfMeasure" value={formData.unitOfMeasure} onChange={handleChange} required options={[{
          value: 'pcs',
          label: 'pcs'
        }, {
          value: 'box',
          label: 'box'
        }, {
          value: 'kg',
          label: 'kg'
        }, {
          value: 'lot',
          label: 'lot'
        }]} data-id="element-1448" />
          <TextField label="Default Rate" name="defaultRate" type="number" value={formData.defaultRate} onChange={handleChange} data-id="element-1449" />
          <TextField label="Warranty" name="warranty" value={formData.warranty} onChange={handleChange} data-id="element-1450" />
          <SelectField label="Status" name="status" value={formData.status} onChange={handleChange} required options={[{
          value: 'Active',
          label: 'Active'
        }, {
          value: 'Inactive',
          label: 'Inactive'
        }]} data-id="element-1451" />
        </FormSection>

        <FormSection title="Additional Notes" data-id="element-1452">
          <TextAreaField label="Notes / Comments" name="notes" value={formData.notes} onChange={handleChange} rows={3} data-id="element-1453" />
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1454">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1455">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1456">
            {mode === 'create' ? 'Save Product' : 'Update Product'}
          </button>
        </div>
      </form>
    </div>;
}